import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, Calendar, Users, DollarSign, LogOut, 
  Sparkles, Lightbulb, ClipboardList, UserCheck, PieChart, Wand2, Bot
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

// AI Tools Component
const AITools = ({ projects, rfps }) => {
  const [activeTab, setActiveTab] = useState('concept');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Concept Generation State
  const [conceptForm, setConceptForm] = useState({
    event_type: '',
    theme: '',
    budget_range: '',
    guest_count: '',
    special_requirements: ''
  });
  
  // Vendor Recommendation State
  const [vendorForm, setVendorForm] = useState({
    event_type: '',
    location: 'Lagos',
    budget_range: ''
  });
  
  // Program Flow State
  const [flowForm, setFlowForm] = useState({
    event_type: '',
    duration_hours: 4,
    activities: ''
  });

  // Budget Analysis State
  const [budgetForm, setBudgetForm] = useState({
    project_id: '',
    estimated_budget: '',
    guest_count: '',
    event_type: '',
    venue_type: ''
  });

  // Proposal Generation State
  const [selectedRfpId, setSelectedRfpId] = useState('');

  const handleGenerateConcept = async () => {
    if (!conceptForm.event_type) {
      toast.error('Please enter an event type');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.generateEventConcept({
        ...conceptForm,
        guest_count: conceptForm.guest_count ? parseInt(conceptForm.guest_count) : null
      });
      setResult({ type: 'concept', data: response.data });
      toast.success('Concept generated successfully!');
    } catch (error) {
      toast.error('Failed to generate concept');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendVendors = async () => {
    if (!vendorForm.event_type || !vendorForm.location) {
      toast.error('Please fill in event type and location');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.recommendVendors(
        vendorForm.event_type,
        vendorForm.location,
        vendorForm.budget_range
      );
      setResult({ type: 'vendors', data: response.data });
      toast.success('Vendor recommendations generated!');
    } catch (error) {
      toast.error('Failed to get vendor recommendations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptimizeFlow = async () => {
    if (!flowForm.event_type || !flowForm.activities) {
      toast.error('Please fill in event type and activities');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const activitiesArray = flowForm.activities.split('\n').filter(a => a.trim());
      const response = await api.optimizeProgramFlow(
        flowForm.event_type,
        flowForm.duration_hours,
        activitiesArray
      );
      setResult({ type: 'flow', data: response.data });
      toast.success('Program flow optimized!');
    } catch (error) {
      toast.error('Failed to optimize program flow');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeBudget = async () => {
    if (!budgetForm.project_id || !budgetForm.estimated_budget) {
      toast.error('Please select a project and enter budget');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.analyzeBudget({
        ...budgetForm,
        estimated_budget: parseFloat(budgetForm.estimated_budget),
        guest_count: parseInt(budgetForm.guest_count) || 100
      });
      setResult({ type: 'budget', data: response.data });
      toast.success('Budget analysis complete!');
    } catch (error) {
      toast.error('Failed to analyze budget');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProposal = async () => {
    if (!selectedRfpId) {
      toast.error('Please select an RFP');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const response = await api.generateProposal(selectedRfpId);
      setResult({ type: 'proposal', data: response.data });
      toast.success('Proposal generated!');
    } catch (error) {
      toast.error('Failed to generate proposal');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'concept', label: 'Event Concept', icon: Lightbulb },
    { id: 'vendors', label: 'Vendor Finder', icon: UserCheck },
    { id: 'flow', label: 'Program Flow', icon: ClipboardList },
    { id: 'budget', label: 'Budget Analysis', icon: PieChart },
    { id: 'proposal', label: 'Proposal Writer', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Bot className="text-[#D4AF37] mr-3" size={28} />
        <div>
          <h2 className="text-2xl font-medium">AI-Powered Tools</h2>
          <p className="text-white/60 text-sm">Leverage AI to streamline your event planning</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setResult(null); }}
            className={`flex items-center px-4 py-2 rounded-lg text-sm transition-all ${
              activeTab === tab.id 
                ? 'bg-[#D4AF37] text-black font-medium' 
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            <tab.icon size={16} className="mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6">
          {activeTab === 'concept' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <Sparkles size={20} className="mr-2" />
                Generate Event Concept
              </h3>
              <p className="text-white/60 text-sm">Create unique event concepts with AI assistance</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Event Type *</label>
                <input
                  type="text"
                  value={conceptForm.event_type}
                  onChange={(e) => setConceptForm({ ...conceptForm, event_type: e.target.value })}
                  placeholder="e.g., Corporate Gala, Wedding, Product Launch"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Theme (Optional)</label>
                <input
                  type="text"
                  value={conceptForm.theme}
                  onChange={(e) => setConceptForm({ ...conceptForm, theme: e.target.value })}
                  placeholder="e.g., Elegant Gold, Modern Minimalist"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/80 text-sm block mb-2">Budget Range</label>
                  <input
                    type="text"
                    value={conceptForm.budget_range}
                    onChange={(e) => setConceptForm({ ...conceptForm, budget_range: e.target.value })}
                    placeholder="e.g., ₦5M - ₦10M"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/80 text-sm block mb-2">Guest Count</label>
                  <input
                    type="number"
                    value={conceptForm.guest_count}
                    onChange={(e) => setConceptForm({ ...conceptForm, guest_count: e.target.value })}
                    placeholder="e.g., 200"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Special Requirements</label>
                <textarea
                  value={conceptForm.special_requirements}
                  onChange={(e) => setConceptForm({ ...conceptForm, special_requirements: e.target.value })}
                  placeholder="Any specific requirements or preferences..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <Button
                onClick={handleGenerateConcept}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Generating...' : 'Generate Concept'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'vendors' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <UserCheck size={20} className="mr-2" />
                Vendor Recommendations
              </h3>
              <p className="text-white/60 text-sm">Get AI-curated vendor suggestions for your event</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Event Type *</label>
                <input
                  type="text"
                  value={vendorForm.event_type}
                  onChange={(e) => setVendorForm({ ...vendorForm, event_type: e.target.value })}
                  placeholder="e.g., Wedding, Corporate Event"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Location *</label>
                <select
                  value={vendorForm.location}
                  onChange={(e) => setVendorForm({ ...vendorForm, location: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                >
                  <option value="Lagos">Lagos</option>
                  <option value="Abuja">Abuja</option>
                  <option value="Port Harcourt">Port Harcourt</option>
                  <option value="Ibadan">Ibadan</option>
                  <option value="Kano">Kano</option>
                </select>
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Budget Range</label>
                <input
                  type="text"
                  value={vendorForm.budget_range}
                  onChange={(e) => setVendorForm({ ...vendorForm, budget_range: e.target.value })}
                  placeholder="e.g., ₦2M - ₦5M"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <Button
                onClick={handleRecommendVendors}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Finding Vendors...' : 'Get Recommendations'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'flow' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <ClipboardList size={20} className="mr-2" />
                Program Flow Optimizer
              </h3>
              <p className="text-white/60 text-sm">Optimize your event timeline and activities</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Event Type *</label>
                <input
                  type="text"
                  value={flowForm.event_type}
                  onChange={(e) => setFlowForm({ ...flowForm, event_type: e.target.value })}
                  placeholder="e.g., Conference, Wedding Reception"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Duration (Hours)</label>
                <input
                  type="number"
                  value={flowForm.duration_hours}
                  onChange={(e) => setFlowForm({ ...flowForm, duration_hours: parseInt(e.target.value) })}
                  min="1"
                  max="24"
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <div>
                <label className="text-white/80 text-sm block mb-2">Activities (One per line) *</label>
                <textarea
                  value={flowForm.activities}
                  onChange={(e) => setFlowForm({ ...flowForm, activities: e.target.value })}
                  placeholder="Registration&#10;Keynote Speech&#10;Networking Break&#10;Panel Discussion&#10;Closing Ceremony"
                  rows={6}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                />
              </div>
              <Button
                onClick={handleOptimizeFlow}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Optimizing...' : 'Optimize Flow'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'budget' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <PieChart size={20} className="mr-2" />
                Budget Risk Analysis
              </h3>
              <p className="text-white/60 text-sm">Analyze and optimize your event budget</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Select Project *</label>
                <select
                  value={budgetForm.project_id}
                  onChange={(e) => setBudgetForm({ ...budgetForm, project_id: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                >
                  <option value="">Select a project...</option>
                  {projects.map(p => (
                    <option key={p.project_id} value={p.project_id}>{p.project_name}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/80 text-sm block mb-2">Estimated Budget (₦) *</label>
                  <input
                    type="number"
                    value={budgetForm.estimated_budget}
                    onChange={(e) => setBudgetForm({ ...budgetForm, estimated_budget: e.target.value })}
                    placeholder="5000000"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/80 text-sm block mb-2">Guest Count</label>
                  <input
                    type="number"
                    value={budgetForm.guest_count}
                    onChange={(e) => setBudgetForm({ ...budgetForm, guest_count: e.target.value })}
                    placeholder="200"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/80 text-sm block mb-2">Event Type</label>
                  <input
                    type="text"
                    value={budgetForm.event_type}
                    onChange={(e) => setBudgetForm({ ...budgetForm, event_type: e.target.value })}
                    placeholder="Corporate Gala"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="text-white/80 text-sm block mb-2">Venue Type</label>
                  <input
                    type="text"
                    value={budgetForm.venue_type}
                    onChange={(e) => setBudgetForm({ ...budgetForm, venue_type: e.target.value })}
                    placeholder="Ballroom Hotel"
                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                  />
                </div>
              </div>
              <Button
                onClick={handleAnalyzeBudget}
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Analyzing...' : 'Analyze Budget'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}

          {activeTab === 'proposal' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-[#D4AF37] flex items-center">
                <FileText size={20} className="mr-2" />
                Proposal Generator
              </h3>
              <p className="text-white/60 text-sm">Generate professional proposals from RFPs</p>
              
              <div>
                <label className="text-white/80 text-sm block mb-2">Select RFP *</label>
                <select
                  value={selectedRfpId}
                  onChange={(e) => setSelectedRfpId(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded px-4 py-2 text-white"
                >
                  <option value="">Select an RFP...</option>
                  {rfps.map(rfp => (
                    <option key={rfp.rfp_id} value={rfp.rfp_id}>
                      {rfp.client_name} - {rfp.event_type}
                    </option>
                  ))}
                </select>
              </div>
              {selectedRfpId && rfps.find(r => r.rfp_id === selectedRfpId) && (
                <div className="bg-white/5 p-4 rounded">
                  <p className="text-sm text-white/80">
                    <strong>Client:</strong> {rfps.find(r => r.rfp_id === selectedRfpId)?.client_name}
                  </p>
                  <p className="text-sm text-white/80">
                    <strong>Event:</strong> {rfps.find(r => r.rfp_id === selectedRfpId)?.event_type}
                  </p>
                  <p className="text-sm text-white/80">
                    <strong>Date:</strong> {rfps.find(r => r.rfp_id === selectedRfpId)?.event_date}
                  </p>
                </div>
              )}
              <Button
                onClick={handleGenerateProposal}
                disabled={loading || !selectedRfpId}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {loading ? 'Generating...' : 'Generate Proposal'}
                <Wand2 size={16} className="ml-2" />
              </Button>
            </div>
          )}
        </div>

        {/* Result Display */}
        <div className="bg-[#0A0A0F] border border-white/5 rounded-lg p-6 min-h-[400px]">
          <h3 className="text-lg font-medium mb-4 flex items-center">
            <Sparkles size={20} className="mr-2 text-[#D4AF37]" />
            AI Result
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : result ? (
            <div className="prose prose-invert max-w-none">
              <div className="text-xs text-white/40 mb-2">
                Generated at: {new Date(result.data.generated_at || result.data.analyzed_at).toLocaleString()}
              </div>
              <div className="whitespace-pre-wrap text-white/80 text-sm leading-relaxed overflow-y-auto max-h-[500px]">
                {result.data.concept || result.data.recommendations || result.data.optimized_flow || result.data.analysis || result.data.proposal}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-white/40">
              <Bot size={48} className="mb-4" />
              <p>Select a tool and generate content</p>
              <p className="text-sm mt-2">AI results will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PMDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [rfps, setRFPs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rfpsRes, projectsRes] = await Promise.all([
        api.getRFPs(),
        api.getProjects(),
      ]);
      setRFPs(rfpsRes.data);
      setProjects(projectsRes.data);
      setStats({
        total_projects: projectsRes.data.length,
        active_projects: projectsRes.data.filter(p => p.status === 'active').length,
        pending_rfps: rfpsRes.data.filter(r => r.status === 'submitted' || r.status === 'reviewing').length,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getPhaseColor = (phase) => {
    const colors = {
      discovery: 'bg-blue-500/20 text-blue-400',
      concept: 'bg-purple-500/20 text-purple-400',
      planning: 'bg-yellow-500/20 text-yellow-400',
      execution: 'bg-green-500/20 text-green-400',
      post_event: 'bg-gray-500/20 text-gray-400',
    };
    return colors[phase] || 'bg-white/10 text-white/60';
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="pm-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0F] border-r border-white/5 p-6 z-50">
        <Link to="/" className="flex items-center space-x-2 mb-10">
          <div className="text-2xl font-bold tracking-tight">
            <span className="text-[#D4AF37]">NEHPETS</span>
            <span className="text-white">+</span>
          </div>
        </Link>

        <div className="space-y-2 mb-10">
          <Link
            to="/dashboard/pm"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <LayoutDashboard size={20} />
            <span>Overview</span>
          </Link>
          <Link
            to="/dashboard/pm/projects"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Calendar size={20} />
            <span>My Projects</span>
          </Link>
          <Link
            to="/dashboard/pm/rfps"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <FileText size={20} />
            <span>RFPs</span>
          </Link>
          <Link
            to="/dashboard/pm/ai-tools"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Sparkles size={20} />
            <span>AI Tools</span>
          </Link>
          <Link
            to="/dashboard/pm/team"
            className="flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-[#D4AF37] hover:bg-white/5 transition-all rounded"
          >
            <Users size={20} />
            <span>Team</span>
          </Link>
        </div>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            className="w-full bg-transparent border border-white/20 text-white hover:bg-white hover:text-black text-xs py-3"
          >
            <LogOut size={16} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Welcome Banner */}
        <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-lg p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-3 animate-pulse"></div>
            <span className="text-white/80">Welcome back, {user?.full_name}!</span>
          </div>
          <span className="text-white/40 text-sm">Project Manager</span>
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <div>
                <div className="mb-8">
                  <h1 className="text-4xl font-medium mb-2">Project Manager Dashboard</h1>
                  <p className="text-white/60">Manage your events and leverage AI tools</p>
                </div>

                {loading ? (
                  <div className="text-white/60">Loading...</div>
                ) : (
                  <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-[#D4AF37] mb-2">{stats?.total_projects || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Total Projects</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-green-400 mb-2">{stats?.active_projects || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Active Projects</div>
                      </div>
                      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded">
                        <div className="text-3xl font-bold text-yellow-400 mb-2">{stats?.pending_rfps || 0}</div>
                        <div className="text-white/60 text-sm uppercase tracking-wider">Pending RFPs</div>
                      </div>
                    </div>

                    {/* AI Tools Quick Access */}
                    <div className="bg-gradient-to-br from-[#D4AF37]/5 to-transparent border border-[#D4AF37]/20 rounded-lg p-6 mb-8">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center">
                          <Sparkles className="text-[#D4AF37] mr-3" size={24} />
                          <h2 className="text-xl font-medium">AI-Powered Tools</h2>
                        </div>
                        <Link to="/dashboard/pm/ai-tools">
                          <Button className="bg-[#D4AF37] hover:bg-[#B8963A] text-black text-sm">
                            Open AI Studio
                          </Button>
                        </Link>
                      </div>
                      <p className="text-white/60 text-sm mb-4">
                        Generate event concepts, get vendor recommendations, optimize program flows, and more.
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {[
                          { icon: Lightbulb, label: 'Event Concepts' },
                          { icon: UserCheck, label: 'Vendor Finder' },
                          { icon: ClipboardList, label: 'Program Flow' },
                          { icon: PieChart, label: 'Budget Analysis' },
                          { icon: FileText, label: 'Proposal Writer' },
                        ].map((tool, i) => (
                          <div key={i} className="bg-white/5 rounded p-3 text-center">
                            <tool.icon className="mx-auto mb-2 text-[#D4AF37]" size={20} />
                            <span className="text-xs text-white/60">{tool.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* My Projects */}
                    <div>
                      <h2 className="text-2xl font-medium mb-4">My Projects</h2>
                      {projects.length === 0 ? (
                        <div className="bg-[#0A0A0F] border border-white/5 p-6 text-center text-white/60 rounded">
                          No projects assigned yet
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {projects.map((project) => (
                            <div
                              key={project.project_id}
                              className="bg-[#0A0A0F] border border-white/5 p-6 rounded hover:border-[#D4AF37]/30 transition-all"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="text-lg font-medium mb-1">{project.project_name}</h3>
                                  <p className="text-white/60 text-sm">{project.event_type}</p>
                                  <p className="text-white/40 text-xs mt-1">Client: {project.client_email}</p>
                                </div>
                                <div className="text-right">
                                  <span className={`text-xs px-3 py-1 rounded-full ${getPhaseColor(project.phase)}`}>
                                    {project.phase?.replace('_', ' ')}
                                  </span>
                                  <p className="text-white/60 text-sm mt-2">{project.event_date}</p>
                                  <p className="text-xs text-[#D4AF37] mt-1">
                                    ₦{project.budget?.estimated?.toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            }
          />
          <Route path="/ai-tools" element={<AITools projects={projects} rfps={rfps} />} />
          <Route path="/projects" element={<div className="text-white">Projects Detail - Coming Soon</div>} />
          <Route path="/rfps" element={<div className="text-white">RFPs - Coming Soon</div>} />
          <Route path="/team" element={<div className="text-white">Team Management - Coming Soon</div>} />
        </Routes>
      </main>
    </div>
  );
};

export default PMDashboard;
